import deprecate from 'gleam-shared/helpers/deprecate'
import toSentence from 'gleam-shared/helpers/toSentence'
import oxfordComma from 'gleam-shared/helpers/oxfordComma'
import { entriesDeep } from 'gleam-shared/helpers/entriesDeep'
import { get, has, pick, omit, hasAny, assign, getAny, hasOnly, isArray, wrapArray, isPlainObject, isObjectLike } from 'gleam-shared/helpers/nodash'

const KNOWN_EXTRA_KEYS = ['meta']
const UNWANTED_TASK_WRAPPER = 'error_message'
const UNWANTED_GENERAL_WRAPPERS = ['error', 'errors']

const LEGACY_DEFAULT_WRAPPER = 'error'
const LEGACY_DEPRECATION_MESSAGE = 'ServerError.response.data.error is deprecated! Use ServerError.cause instead'

export default function toServerError(response, reason) {
  reason = unwrapTask(reason)
  const cause = formatCause(reason)
  const extra = formatExtra(reason)

  const legacyData = formatLegacyData(reason)
  const extendedResponse = assign(response, { data: legacyData })
  const deprecatedResponse = deprecate(extendedResponse, 'data', LEGACY_DEPRECATION_MESSAGE)

  return assign(new Error(formatMessage(response, cause, extra)), {
    name: 'ServerError',
    cause,
    response: deprecatedResponse,
    ...extra
  })
}

function unwrapTask(input) {
  if (has(input, UNWANTED_TASK_WRAPPER)) {
    return get(input, UNWANTED_TASK_WRAPPER)
  } else {
    return input
  }
}

function formatCause(reason) {
  if (isPlainObject(reason)) {
    if (hasAny(reason, UNWANTED_GENERAL_WRAPPERS)) {
      return getAny(reason, UNWANTED_GENERAL_WRAPPERS)
    } else if (hasOnly(reason, KNOWN_EXTRA_KEYS)) {
      return undefined
    } else {
      return omit(reason, KNOWN_EXTRA_KEYS)
    }
  } else {
    return reason
  }
}

function formatExtra(reason) {
  if (isPlainObject(reason)) {
    if (hasAny(reason, UNWANTED_GENERAL_WRAPPERS)) {
      return omit(reason, UNWANTED_GENERAL_WRAPPERS)
    } else {
      return pick(reason, KNOWN_EXTRA_KEYS)
    }
  } else {
    return {}
  }
}

function formatLegacyData(reason) {
  if (hasAny(reason, UNWANTED_GENERAL_WRAPPERS) || hasAny(reason, KNOWN_EXTRA_KEYS)) {
    return reason
  } else {
    return { [LEGACY_DEFAULT_WRAPPER]: reason }
  }
}

function formatMessage(response, cause, extra) {
  const ref = response.url || 'The server'
  const base = `${ref} replied with ${response.statusText} (HTTP ${response.status})`
  const reports = formatReports(cause, extra)

  if (reports) {
    return `${base}: ${reports}`
  } else {
    return base
  }
}

function formatReports(cause, extra) {
  const causeReport = formatCauseReport(cause)
  const extraReport = formatExtraReport(extra)

  if (causeReport && extraReport) {
    return `${toSentence(causeReport)} ${toSentence(extraReport)}`
  } else {
    return causeReport || extraReport
  }
}

function formatCauseReport(cause) {
  if ((isPlainObject(cause) && hasPlainObjects(cause)) || (isArray(cause) && hasObjects(cause))) {
    return formatCauseEntries(entriesDeep(cause))
  } else if (isPlainObject(cause)) {
    return formatCauseEntries(Object.entries(cause))
  } else {
    return formatCauseArray(wrapArray(cause))
  }
}

function formatCauseArray(array) {
  return array.map(toSentence).join(' ')
}

function formatCauseEntries(entries) {
  return entries.map(([key, val]) => {
    const formattedKey = quoteString(key)
    const formattedVal = oxfordComma(wrapArray(val).map(quoteString))
    return `${formattedKey} says ${formattedVal}`
  }).join('; ')
}

function formatExtraReport(extra) {
  return entriesDeep(extra).map(([key, val]) => {
    const formattedKey = quoteString(key)
    const formattedVal = quoteString(val)
    return `${formattedKey} is ${formattedVal}`
  }).join('; ')
}

function hasPlainObjects(input) {
  return Object.values(input).some(isPlainObject)
}

function hasObjects(input) {
  return Object.values(input).some(isObjectLike)
}

function quoteString(string) {
  return `"${string}"`
}
